import { FC, useMemo } from 'react'
import { GetServerSideProps } from 'next'
import { useRouter } from 'next/router'
import Link from 'next/link'

import MainLayout from 'containers/main-layout/main-layout'
import LikeWrap from 'containers/like-wrap/like-wrap'
import WidthLimiter from 'containers/width-limiter/width-limiter'
import ClinicalCard from 'components/cards/clinical-card/clinical-card'
import Commentaries from 'components/parts/commentaries/commentaries'
import SEOMetatags from 'components/parts/seo-metatags/seo-metatags'
import SEOBreadCrumbs from 'components/parts/seo-metatags/seo-breadcrumbs'
import AttachedItems from 'components/parts/attached-items/attached-items'
import Button from 'components/parts/button/button'
import NoAcceessBanner from 'components/banners/no_access_banner/no_access_banner'
import BreadcrumbsPage from 'components/parts/breadcrumb-page/breadcrumb-page'
import useSetView from 'hooks/useSetView'
import useRecommendation from 'hooks/useRecomendation'
import { useAuth } from 'store/auth/useAuth'
import SEOContext from 'context/SEOContext'

import { getDigitFromString } from 'utils/getURLData'
import nestRequest from 'utils/server-side/nestRequest'
import { go404 } from 'utils/redirect'
import { ClinicalCasePageSEO } from 'constants/SEO'
import { ClinicalCaseBC } from 'constants/SEO-breadcrumbs'
import { Pathname, URL_SITE } from 'constants/data'
import {
  useARegAndLogin,
  originMedpulse,
  A_LOG_REG_HASH,
  originReg,
} from 'utils/analytics/other/registration/registration'

import styles from 'styles/clinical-case.module.scss'
import { A_cc_openClinCase, originClinCase } from 'utils/analytics/clincase/clincase'

interface IClinicalCase {
  clinicCaseData: any
}

const ClinicalCasePage: FC<IClinicalCase> = ({ clinicCaseData }: IClinicalCase) => {
  const router = useRouter()
  const { id, Comments, filesList, text, Specialities } = clinicCaseData
  const { isAuthorized } = useAuth()

  const SEOData = useMemo(() => {
    let speciality = Specialities?.[0]?.name
    speciality = speciality ? `${speciality}: ` : ''

    return {
      TITLE: `${speciality}${ClinicalCasePageSEO.TITLE.toLowerCase()} №${id}`,
      DESCRIPTION: text.slice(0, 200),
      LINK: ClinicalCasePageSEO.LINK + `/${id}`,
      IMG: '',
      id,
    }
  }, [id])

  const SEOClinicalCaseBC = useMemo(() => ClinicalCaseBC(id), [id])

  useSetView({ materialId: id, materialType: 'clinicalCase' })
  const { recommendations } = useRecommendation(id, 'clinicalCase', 4)

  const goToClinicalCases = () => {
    router.push(`/${Pathname.CLINICAL_CASE}`)
  }

  const { cards, isLoaded } = recommendations

  useARegAndLogin(originReg.CLINICALCASE)

  return (
    <>
      <SEOMetatags data={SEOData} />
      <SEOBreadCrumbs data={SEOClinicalCaseBC} />

      <SEOContext.Provider value={SEOData}>
        <LikeWrap>
          <MainLayout>
            <WidthLimiter noLeftPadding noRightPadding>
              <div className={styles.breadcrumb}>
                <BreadcrumbsPage
                  breadData={[
                    { name: 'Клинические случаи', link: `${URL_SITE}/clinical-case` },
                    { name: SEOData.TITLE, link: '' },
                  ]}
                ></BreadcrumbsPage>
              </div>
              <div className={styles.clinicalCase}>
                <div className={styles.clinicalWrapper}>
                  {!isAuthorized && (
                    <div
                      className={styles.noaccessWrap}
                      onClick={() => sessionStorage.setItem(A_LOG_REG_HASH, originMedpulse.LOGIN)}
                    >
                      <NoAcceessBanner />
                    </div>
                  )}
                  {isAuthorized && <ClinicalCard {...clinicCaseData} isCasePage />}
                  {isAuthorized && filesList && Boolean(filesList.length) && (
                    <div className={styles.attached}>
                      <AttachedItems items={filesList} />
                    </div>
                  )}
                  <div className={styles.comments}>
                    <Commentaries
                      comments={Comments}
                      materialType="clinicalCase"
                      id={id}
                      widthLimited
                    />
                  </div>
                  <h3 className={styles.recommendHeader}>Может быть интересно</h3>
                  {cards.map((card: any) => {
                    const href = isLoaded ? `/${Pathname.CLINICAL_CASE}/${card.id}` : '#'

                    return (
                      <div
                        key={card.id}
                        className={styles.cardWrapper}
                        onClick={() => A_cc_openClinCase(originClinCase.LANDING, card.id)}
                      >
                        <Link href={href} key={card.id} prefetch={false}>
                          <ClinicalCard {...card} />
                        </Link>
                      </div>
                    )
                  })}
                  <div className={styles.sendBtn} onClick={goToClinicalCases}>
                    <Button>Рассказать о своем случае коллегам</Button>
                  </div>
                </div>
              </div>
            </WidthLimiter>
          </MainLayout>
        </LikeWrap>
      </SEOContext.Provider>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  let clinicCaseData = null

  if (context?.params?.id) {
    try {
      const materialId = getDigitFromString(context.params.id as string)
      const restRet = await nestRequest(context, 'get', `/api/clinic/${materialId}`, {})
      clinicCaseData = restRet?.data

      if (clinicCaseData.id == 61) {
        return {
          redirect: {
            destination: `/${Pathname.CLINICAL}/60`,
            statusCode: 307,
          },
        }
      }
      if (!clinicCaseData) {
        return go404()
      }
    } catch (err) {
      console.warn(err)
      return go404()
    }
  }

  return {
    props: {
      clinicCaseData: {
        ...clinicCaseData,
        text: clinicCaseData.text.replaceAll('\n', '<br/>'),
      },
    },
  }
}

export default ClinicalCasePage
